import { useState } from "react";
import { CheckCircle2, Sparkles, RotateCcw, ArrowRight } from "lucide-react";
import { PlayCanvas } from "@/components/field/PlayCanvas";

const hidePlayer = (play, id) => ({ ...play, players: play.players.filter((p) => p.id !== id), routes: [], ball: { carrier: null, events: [] } });

export function QuizRunner({ play, team, posId, onReplay }) {
  const qs = play.quiz.filter((q) => !q.position_id || q.position_id === posId);
  const [i, setI] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const q = qs[i];
  if (!qs.length) return <p className="card p-5 text-slate-400" data-testid="quiz-empty">No questions for this position yet. Pick another position or ask your coach.</p>;
  if (!q) return (
    <div className="card p-6 text-center space-y-3" data-testid="quiz-done">
      <Sparkles size={36} className="mx-auto text-[#FACC15]" />
      <p className="h2">All done! You got {score} of {qs.length}.</p>
      <p className="text-slate-300">Great practice. Keep watching your job and you'll have it down.</p>
      <button className="btn btn-primary" onClick={() => { setI(0); setScore(0); setAnswer(null); }} data-testid="btn-quiz-restart">Try again</button>
    </div>
  );

  const submit = (val) => {
    if (answer) return;
    const ok = q.kind === "choice" ? val === q.correct : Math.hypot(val.x - q.target.x, val.y - q.target.y) <= q.target.r;
    setAnswer({ val, ok });
    if (ok) setScore(score + 1);
  };
  const next = () => { setAnswer(null); setI(i + 1); };
  const fieldPlay = q.kind === "spot" && !answer && q.position_id ? hidePlayer(play, q.position_id) : play;

  return (
    <div className="space-y-3" data-testid="quiz-runner">
      <div className="card p-4">
        <p className="label">Question {i + 1} of {qs.length}</p>
        <p className="text-xl font-bold" data-testid="quiz-prompt">{q.prompt}</p>
        {q.kind === "choice" ? (
          <div className="grid sm:grid-cols-2 gap-2 mt-4">
            {q.options.map((o, j) => (
              <button key={j} className={`btn justify-start text-base min-h-[56px] ${answer && j === q.correct ? "border-2 border-[#22C55E] bg-[#22C55E]/15" : answer?.val === j ? "border-2 border-[#FACC15] bg-[#FACC15]/10" : "btn-ghost"}`} onClick={() => submit(j)} data-testid={`btn-submit-quiz-answer-${j}`}>{o}</button>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400 mt-2">Tap the field to answer.</p>}
      </div>
      {q.kind === "spot" && (
        <div className="rounded-2xl overflow-hidden border border-[#1F3358]">
          <PlayCanvas play={fieldPlay} team={team} showRoutes={false} quizTarget={answer ? q.target : null} tapMark={answer ? { ...answer.val, ok: answer.ok } : null} onFieldDown={answer ? undefined : (pt) => submit(pt)} />
        </div>
      )}
      {answer && (
        <div className={`card p-4 tpc-rise ${answer.ok ? "border-[#22C55E]" : "border-[#FACC15]"}`} data-testid="quiz-feedback">
          <p className="text-lg font-bold flex items-center gap-2">{answer.ok ? <><CheckCircle2 className="text-[#22C55E]" />Nice work! You got it.</> : <><Sparkles className="text-[#FACC15]" />Good try! Let's look again.</>}</p>
          {!answer.ok && q.hint && <p className="mt-1 text-slate-300" data-testid="quiz-hint">Hint: {q.hint}</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            {posId && <button className="btn btn-ghost" onClick={onReplay} data-testid="quiz-replay-assignment"><RotateCcw size={16} />Replay my assignment</button>}
            <button className="btn btn-primary" onClick={next} data-testid="btn-quiz-next">Next<ArrowRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
